import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/hooks/useLanguage";
import { usePremiumGate } from "@/hooks/usePremiumGate";
import { listCourses, generateLesson, listGeneratedLessons, deleteGeneratedLesson, type Course, type GeneratedLesson } from "@/lib/courses";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpen, Sparkles, Clock, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { LessonBody } from "@/components/LessonBody";

export const Route = createFileRoute("/courses/")({
  component: CoursesPage,
  head: () => ({
    meta: [
      { title: "Courses & Lessons - Inquira" },
      { name: "description", content: "Guided study paths in algebra, geometry and calculus, with practice and quizzes after every lesson." },
      { property: "og:title", content: "Courses & Lessons - Inquira" },
      { property: "og:description", content: "Step-by-step study paths plus AI lessons generated on any topic you choose." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/courses" }],
  }),
});

function CoursesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { requirePremium } = usePremiumGate();
  const [courses, setCourses] = useState([] as Course[]);
  const [generated, setGenerated] = useState([] as GeneratedLesson[]);
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null as string | null);

  useEffect(() => {
    listCourses().then(setCourses).catch(() => setCourses([]));
  }, []);

  useEffect(() => {
    if (!user) { setGenerated([]); return; }
    listGeneratedLessons(user.id).then(setGenerated).catch(() => setGenerated([]));
  }, [user]);

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    if (!requirePremium("lesson_generate")) return;
    setBusy(true);
    try {
      await generateLesson(topic.trim(), language.code);
      setTopic("");
      if (user) setGenerated(await listGeneratedLessons(user.id));
      toast.success(t("courses.lessonReady"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("courses.generateFailed"));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteGeneratedLesson(id);
      setGenerated((prev) => prev.filter((lesson) => lesson.id !== id));
    } catch {
      toast.error(t("courses.deleteFailed"));
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 space-y-10">
      <header className="space-y-2">
        <h1 className="text-3xl font-bold">{t("courses.title")}</h1>
        <p className="text-muted-foreground">{t("courses.subtitle")}</p>
      </header>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold">{t("courses.pathsHeading")}</h2>
        <ul className="space-y-3">
          {courses.map((course) => (
            <li key={course.id}>
              <Link
                to="/courses/$courseSlug"
                params={{ courseSlug: course.slug }}
                className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 transition hover:border-primary"
              >
                <BookOpen className="mt-1 size-5 shrink-0 text-primary" aria-hidden="true" />
                <span className="flex-1">
                  <span className="block font-semibold">{course.title}</span>
                  <span className="block text-sm text-muted-foreground">{course.summary}</span>
                  <span className="mt-1 block text-xs uppercase tracking-wide text-muted-foreground">
                    {course.subject} · {t(`courses.level.${course.level}`, course.level)}
                  </span>
                </span>
                <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground rtl:rotate-180" aria-hidden="true" />
              </Link>
            </li>
          ))}
          {courses.length === 0 && <li className="text-sm text-muted-foreground">{t("common.loading")}</li>}
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold">{t("courses.generateHeading")}</h2>
        <p className="text-sm text-muted-foreground">{t("courses.generateHint")}</p>
        <div className="flex gap-2">
          <Input
            value={topic}
            onChange={(event) => setTopic(event.target.value)}
            placeholder={t("courses.topicPlaceholder")}
            aria-label={t("courses.topicPlaceholder")}
          />
          <Button onClick={handleGenerate} disabled={busy || !topic.trim()}>
            <Sparkles className="size-4" aria-hidden="true" />
            {busy ? t("courses.generating") : t("courses.generate")}
          </Button>
        </div>

        {generated.length > 0 && (
          <ul className="space-y-3">
            {generated.map((lesson) => (
              <li key={lesson.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    className="flex-1 text-start"
                    onClick={() => setOpen(open === lesson.id ? null : lesson.id)}
                  >
                    <span className="block font-semibold">{lesson.title}</span>
                    <span className="block text-sm text-muted-foreground">{lesson.objective}</span>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("courses.deleteLesson")}
                    onClick={() => void handleDelete(lesson.id)}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </div>
                {open === lesson.id && (
                  <div className="mt-4 border-t border-border pt-4">
                    <LessonBody sections={lesson.sections} practice={lesson.practice} quiz={lesson.quiz} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Clock className="size-3" aria-hidden="true" />
        {t("courses.footnote")}
      </p>
    </div>
  );
}
